/**
 * Performance Tab PDF Export Utility
 *
 * Exports the Performance Scorecard as a multi-page PDF:
 * - Page 1: Scorecard metrics and supplier ratings (portrait)
 * - Page 2: Daily OTIF chart (portrait)
 *
 * Naming convention: SupplierPerformance_[Supplier]_[Month]_[Year].pdf
 *
 * Key principle: Capture UI exactly as rendered, then scale to fit PDF.
 * This preserves the exact appearance including chart data alignment.
 */
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'

/** JPEG quality for embedded images (0.0 to 1.0) */
const IMAGE_QUALITY = 0.92

/** Canvas scale factor for crisp output */
const CANVAS_SCALE = 2

interface ExportOptions {
  supplierName: string
  month: string
  year: number | string
  scorecardElement: HTMLElement
  chartElement?: HTMLElement | null
}

/**
 * Sanitize filename by removing invalid characters
 */
function sanitizeFilename(name: string): string {
  return name
    .replace(/[/\\:*?"<>|]/g, '')
    .replace(/[-\s]+/g, '_')
    .replace(/_+/g, '_')
    .trim()
}

/**
 * Generate PDF filename
 */
function generateFilename(supplierName: string, month: string, year: number | string): string {
  const sanitizedSupplier = sanitizeFilename(supplierName || 'AllSuppliers')
  return `SupplierPerformance_${sanitizedSupplier}_${month}_${year}.pdf`
}

/**
 * Add styled header to PDF page
 */
function addPdfHeader(
  pdf: jsPDF,
  title: string,
  subtitle: string,
  pageWidth: number,
  margin: number
): number {
  pdf.setFontSize(16)
  pdf.setFont('helvetica', 'bold')
  pdf.setTextColor(33, 33, 33)
  pdf.text(title, pageWidth / 2, margin + 8, { align: 'center' })

  pdf.setFontSize(10)
  pdf.setFont('helvetica', 'normal')
  pdf.setTextColor(100, 100, 100)
  pdf.text(subtitle, pageWidth / 2, margin + 15, { align: 'center' })

  pdf.setDrawColor(59, 130, 246)
  pdf.setLineWidth(0.5)
  pdf.line(margin, margin + 20, pageWidth - margin, margin + 20)

  return margin + 25
}

/**
 * Add footer to PDF page
 */
function addPdfFooter(pdf: jsPDF, pageWidth: number, pageHeight: number, margin: number): void {
  const footerY = pageHeight - 8
  pdf.setFontSize(8)
  pdf.setFont('helvetica', 'normal')
  pdf.setTextColor(150, 150, 150)

  const dateStr = new Date().toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
  pdf.text(`Generated: ${dateStr}`, margin, footerY)

  const pageNum = pdf.getCurrentPageInfo().pageNumber
  pdf.text(`Page ${pageNum}`, pageWidth - margin, footerY, { align: 'right' })
}

/**
 * Capture element exactly as displayed and fit to PDF area
 */
async function captureElement(
  element: HTMLElement,
  pdf: jsPDF,
  availableWidth: number,
  availableHeight: number,
  yPosition: number
): Promise<void> {
  // Capture element exactly as it appears - no dimension changes
  const canvas = await html2canvas(element, {
    scale: CANVAS_SCALE,
    useCORS: true,
    logging: false,
    backgroundColor: '#ffffff',
    // Remove any scrolling offset issues
    scrollX: 0,
    scrollY: -window.scrollY,
  })

  const imgData = canvas.toDataURL('image/jpeg', IMAGE_QUALITY)

  // Calculate scaling to fit available area while maintaining aspect ratio
  const canvasAspect = canvas.width / canvas.height
  let imgWidth = availableWidth
  let imgHeight = imgWidth / canvasAspect

  // If too tall, scale by height instead
  if (imgHeight > availableHeight) {
    imgHeight = availableHeight
    imgWidth = imgHeight * canvasAspect
  }

  // Center horizontally
  const pageWidth = pdf.internal.pageSize.getWidth()
  const xOffset = (pageWidth - imgWidth) / 2

  pdf.addImage(imgData, 'JPEG', xOffset, yPosition, imgWidth, imgHeight)
}

/**
 * Export Performance Scorecard as a multi-page PDF
 */
export async function exportPerformancePDF(options: ExportOptions): Promise<void> {
  const { supplierName, month, year, scorecardElement, chartElement } = options

  const filename = generateFilename(supplierName, month, year)

  // Create PDF in A4 portrait with compression
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
    compress: true,
  })

  const margin = 10
  const pageWidth = pdf.internal.pageSize.getWidth()
  const pageHeight = pdf.internal.pageSize.getHeight()
  const footerSpace = 12

  // Page 1: Scorecard
  const yAfterHeader = addPdfHeader(
    pdf,
    'Supplier Performance Report',
    `${supplierName} | ${month} ${year}`,
    pageWidth,
    margin
  )

  const availableWidth = pageWidth - margin * 2
  const availableHeight = pageHeight - yAfterHeader - margin - footerSpace

  await captureElement(scorecardElement, pdf, availableWidth, availableHeight, yAfterHeader)
  addPdfFooter(pdf, pageWidth, pageHeight, margin)

  // Page 2: Chart
  if (chartElement) {
    pdf.addPage('a4', 'portrait')

    const chartYAfterHeader = addPdfHeader(
      pdf,
      'Daily OTIF Analysis',
      `${supplierName} | ${month} ${year}`,
      pageWidth,
      margin
    )

    const chartAvailableHeight = pageHeight - chartYAfterHeader - margin - footerSpace

    await captureElement(chartElement, pdf, availableWidth, chartAvailableHeight, chartYAfterHeader)
    addPdfFooter(pdf, pageWidth, pageHeight, margin)
  }

  pdf.save(filename)
}
